import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { recoveryService } from "../../services/api";

interface RecoveryState {
  requests: any[];
  isLoading: boolean;
  error: string | null;
}

const initialState: RecoveryState = {
  requests: [],
  isLoading: false,
  error: null,
};

export const submitRecovery = createAsyncThunk(
  "recovery/submit",
  async (recoveryData: any, { rejectWithValue }) => {
    try {
      const response = await recoveryService.submitRecovery(recoveryData);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to submit recovery request"
      );
    }
  }
);

export const getRecoveryRequests = createAsyncThunk(
  "recovery/get",
  async (filters: any = {}, { rejectWithValue }) => {
    try {
      const response = await recoveryService.getUserRecoveryRequests(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to get recovery requests"
      );
    }
  }
);

const recoverySlice = createSlice({
  name: "recovery",
  initialState,
  reducers: {
    clearRecoveryError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitRecovery.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(submitRecovery.fulfilled, (state, action) => {
        state.isLoading = false;

        // SAFETY: only push if request exists
        if (action.payload?.request) {
          state.requests.unshift(action.payload.request);
        }
      })
      .addCase(submitRecovery.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(getRecoveryRequests.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getRecoveryRequests.fulfilled, (state, action) => {
        state.isLoading = false;

        /**
         * NORMALIZATION LAYER
         * Ensures requests is ALWAYS an array
         */
        if (Array.isArray(action.payload)) {
          state.requests = action.payload;
        } else if (Array.isArray(action.payload?.data)) {
          state.requests = action.payload.data;
        } else if (Array.isArray(action.payload?.requests)) {
          state.requests = action.payload.requests;
        } else {
          state.requests = [];
        }
      })
      .addCase(getRecoveryRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.requests = [];
      });
  },
});

export const { clearRecoveryError } = recoverySlice.actions;
export default recoverySlice.reducer;
